import { useEffect, useRef, useState } from 'react'
import { ChartResponse } from './types-v2'
import { VegaLite, VisualizationSpec, createClassFromSpec } from 'react-vega'
import { InferFormValues, JsonFormSchemaV2 } from '../FormComponent/types'
import { ComponentTitle } from '../ComponentTitle'
import { TileFilter } from '../TileFilter'

export const ChartContent = ({
  data,
  tileFilterSchema,
  filterChangeHandler,
}: {
  data: ChartResponse
  tileFilterSchema?: JsonFormSchemaV2
  filterChangeHandler?: (data: InferFormValues<JsonFormSchemaV2>) => void
}) => {
  // Get the spec from the uiSchema
  const originalSpec = data.uiSchema.spec
  const containerRef = useRef<HTMLDivElement>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 600, height: 300 })
  const [specReady, setSpecReady] = useState(false)

  // Create a deep clone of the spec so we can safely modify it
  const spec = originalSpec ? JSON.parse(JSON.stringify(originalSpec)) : null

  // Margins to prevent overflow
  const RIGHT_MARGIN = 90 // Increased to account for the extra space for actions button
  const TOP_MARGIN = 40
  const BOTTOM_MARGIN = 40 // Add bottom margin for action details

  // Creating a compiled chart component to avoid CSP 'unsafe-eval' issues
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [CompiledChart, setCompiledChart] = useState<React.ComponentType<any> | null>(null)

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        // Only update if dimensions are valid and different from current
        if (
          rect.width > 0 &&
          rect.height > 0 &&
          (rect.width !== dimensions.width || rect.height !== dimensions.height)
        ) {
          setDimensions({
            width: rect.width,
            height: rect.height,
          })
        }
      }
    }

    // Initial measurement
    updateDimensions()

    // Mark the spec as ready after initial render
    const readyTimer = setTimeout(() => {
      setSpecReady(true)
      // Trigger another dimension update after the component is fully rendered
      updateDimensions()
    }, 100)

    // Set up resize observer
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions()
      // Also trigger a manual resize event for Vega-Lite
      window.dispatchEvent(new Event('resize'))
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    // Handle window resize
    window.addEventListener('resize', updateDimensions)

    // Cleanup
    return () => {
      clearTimeout(readyTimer)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateDimensions)
    }
  }, [dimensions.width, dimensions.height])

  useEffect(() => {
    // Only attempt to compile when spec is ready
    if (spec && specReady) {
      try {
        // Create a compiled version of the chart that doesn't use eval()
        const ChartClass = createClassFromSpec({ mode: 'vega-lite', spec: spec as VisualizationSpec })
        setCompiledChart(() => ChartClass)
      } catch (error) {
        console.error('Failed to compile Vega spec:', error)
      }
    }
  }, [spec, specReady])

  // Modify the spec to use explicit dimensions instead of "container"
  if (spec && specReady) {
    // If the original spec has width: "container", replace it with the measured width
    if (spec.width === 'container') {
      // Subtract margin to prevent overflow
      spec.width = Math.max(dimensions.width - RIGHT_MARGIN, 300)
    }

    // If the original spec has height: "container", replace it with the measured height
    if (spec.height === 'container') {
      spec.height = Math.max(dimensions.height - TOP_MARGIN - BOTTOM_MARGIN, 200)
    }

    // Add padding configuration if it doesn't exist
    if (!spec.padding) {
      spec.padding = { left: 10, top: 10, right: 30, bottom: 10 }
    } else {
      // Ensure right padding is at least 30px
      spec.padding.right = Math.max(spec.padding.right || 0, 30)
    }
  }

  // Custom Vega actions configuration
  const vegaActions = {
    export: true,
    source: true,
    compiled: true,
    editor: true,
  }

  // CSS classes to apply to make the actions menu stay within bounds
  const chartContainerStyles = {
    vegaActionsContainer: `
      .vega-embed {
        position: relative;
        overflow: visible;
      }
      .vega-embed details {
        position: absolute;
        top: 0;
        right: 20px; /* Add 20px spacing from the right edge */
        max-width: 100%;
        box-sizing: border-box;
      }
      /* Style the button itself to make it more visible */
      .vega-embed details summary {
        padding: 3px 5px;
        border-radius: 3px;
        background-color: rgba(255, 255, 255, 0.8);
        border: 1px solid #ddd;
      }
    `,
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex w-full flex-wrap items-center justify-between gap-4 border-b pb-4">
        <ComponentTitle title={data.uiSchema.title} description={data.uiSchema.description} />
        <TileFilter schema={tileFilterSchema} submitHandler={filterChangeHandler} />
      </div>
      <div
        ref={containerRef}
        className="h-full w-full"
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '300px', // Minimum height to account for actions
          height: '100%', // Take full height of parent
          minWidth: '300px',
          paddingBottom: '40px', // Add padding for the actions menu at the bottom
          paddingRight: '20px', // Add right padding to container
          overflow: 'visible', // Allow actions menu to be visible
          boxSizing: 'border-box', // Include padding in height calculation
        }}
      >
        {/* Style to contain actions */}
        <style>{chartContainerStyles.vegaActionsContainer}</style>

        {/* Chart container with nested div for VegaLite */}
        <div
          ref={chartContainerRef}
          style={{
            position: 'relative',
            flexGrow: 1, // Take available space
            width: '100%',
            height: '100%',
          }}
        >
          {/* Use CompiledChart when available, otherwise fallback to VegaLite or loading state */}
          {spec && specReady && dimensions.width > 0 && dimensions.height > 0 ? (
            CompiledChart ? (
              <CompiledChart ast={true} actions={vegaActions} />
            ) : (
              <VegaLite ast={true} spec={spec} actions={vegaActions} />
            )
          ) : (
            <div className="flex h-full items-center justify-center text-gray-500">
              {!spec ? 'No chart data available' : 'Loading chart...'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
